/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';

/**
 * Přehrávač plánování.
 * Stará se časovač využívaný k odpočtu času pro přehrávání slajdů.
 * @implements {Destroyable}
 * @implements {EventBus}
 */
class SchedulePlayer {
  /**
   * Vytvoří instanci.
   * @returns {SchedulePlayer} Nová instance.
   */
  constructor() {
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    /**
     * Příznak, určující jestli probíhá přehrávání.
     * @private
     * @type {boolean}
     */
    this.playing = false;
    /**
     * Časovač pro přehrávání.
     * @private
     * @type {?number}
     */
    this.timeout = null;
  }

  /** @inheritdoc */
  destroy() {
    clearTimeout(this.timeout);
    this.timeout = null;
  }

  /**
   * Určí, jestli probíhá přehrávání.
   * @returns {boolean} Příznak, jestli probíhá přehrávání.
   */
  isPlaying() {
    return this.playing;
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na vypršení časovače.
   * @private
   * @returns {undefined} Nic.
   */
  onTimeout() {
    this.playing = false;
    this.timeout = null;
    this.emitter.emit('played');
  }

  /**
   * Spustí přehrávání.
   * Pokud je určena délka přehrávání, tak se naplánuje konec.
   * Pokud není, tak je přehráváno navždy.
   * @param {null|number} duration Doba přehrávání.
   * @returns {undefined} Nic.
   */
  play(duration) {
    if (!isNil(duration) && !isNumber(duration)) {
      throw new TypeError(`Invalid duration ${duration}.`);
    }
    if (this.playing) {
      throw new Error('Already playing.');
    }
    if (duration > 0) {
      this.timeout = setTimeout(() => this.onTimeout(), duration);
    }
    this.playing = true;
  }

  /**
   * Zastaví přehrávání.
   * @returns {undefined} Nic.
   */
  stop() {
    clearTimeout(this.timeout);
    this.playing = false;
    this.timeout = null;
  }
}

/**
 * Uplynutí časovače přehrávání.
 * @event SchedulePlayer#played
 */

export default SchedulePlayer;
